import React from 'react';

import { Teammate } from '@flipdish/api-client-typescript';
import * as FlipdishAPI from '@flipdish/api-client-typescript';
import Fab from '@material-ui/core/Fab';
import Hidden from '@material-ui/core/Hidden';
import Snackbar from '@material-ui/core/Snackbar';
import SnackbarContent from '@material-ui/core/SnackbarContent';
import { Theme, withStyles } from '@material-ui/core/styles';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import withWidth from '@material-ui/core/withWidth';
import AddIcon from '@material-ui/icons/Add';
import DoneIcon from '@material-ui/icons/Done';
import classNames from 'classnames';
import moment from 'moment';
import { getTranslate } from 'react-localize-redux';
import { connect } from 'react-redux';
import Permissions from 'react-redux-permissions';
import { Link } from 'react-router-dom';
import { compose } from 'redux';

import { reinviteTeammate } from '../../actions/teammates.actions';
import EmptyIcon from '../../assets/images/empty-teammates.svg';
import { capitalizeFirst } from '../../helpers/utilities';
import { getSelectedApp } from '../../selectors/app.selector';
import { permissionsSelector } from '../../selectors/permissions.selector';
import EmptyComponent from '../../ui/EmptyComponent';
import PageLayout from '../../ui/Layout';
import GridContainer from '../../ui/Layout/GridContainer';
import PaperContainer from '../../ui/Layout/PaperContainer';
import TableCellLink from '../../ui/TableCellLink/TableCellLink';
import { AppLoadingCircularProgress } from '../AppLoadingCircularProgress';
import { ListMobile } from './ListMobile';
import { getCurrentUser, getTeammates, getTeammatesTimestamp } from './selectors';

const AppResourceSetEnum = FlipdishAPI.App.AppResourceSetEnum;

const styles = (theme: Theme) => ({
  button: {
    margin: theme.spacing(1),
    color: 'white',
    position: 'fixed' as any,
    bottom: '24px',
    right: '24px',
    backgroundColor: '#ff0046',
    fontSize: '14px',
    fontWeight: '500' as any,
    fontStyle: 'normal',
    fontStretch: 'normal',
    lineHeight: '1.14',
    letterSpacing: '1.3px',
  },
  extendedIcon: {
    marginRight: theme.spacing(1),
  },
  table: {
    width: '100%',
  },
  row: {
    '&:nth-of-type(odd)': {
      backgroundColor: '#fafafa',
    },
    '&:hover': {
      backgroundColor: '#ece6f7',
    },
  },
  rowLink: {
    cursor: 'pointer',
  },
  cell: {
    padding: '16px 24px',
    borderBottomColor: 'rgba(0,0,0,0.21)',
  },
  headCell: {
    fontStyle: 'normal',
    fontStretch: 'normal',
    lineHeight: '2',
  },
  bodyCell: {
    fontWeight: 'normal' as any,
    fontStyle: 'normal',
    fontStretch: 'normal',
    lineHeight: '1.85',
    letterSpacing: 'normal',
    padding: 0,
    height: '100%',
  },
  bodyCellWithPadding: {
    padding: '16px 24px',
  },
  resendInvite: {
    cursor: 'pointer',
    marginLeft: '5px',
    color: '#ff395f',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  inviteResent: {
    cursor: 'pointer',
    marginLeft: '5px',
    color: '#27bf79',
  },
  snackbarContent: {
    backgroundColor: 'rgba(0, 0, 0, 0.87)',
    padding: '16px',
    minWidth: '160px',
    marginBottom: '20px',
    '&>div': {
      padding: '0',
    },
    [theme.breakpoints.down('sm')]: {
      marginLeft: '16px',
      marginRight: '16px',
    },
  },
  snackbarSpan: {
    display: 'flex',
    alignItems: 'center',
    fontSize: '14px',
    fontWeight: 'normal' as any,
    fontStyle: 'normal',
    fontStretch: 'normal',
    lineHeight: '1.43',
    letterSpacing: '0.3px',
  },
  snackbarIcon: {
    width: '22px',
    marginRight: '12px',
  },
  headline: {
    [theme.breakpoints.down('sm')]: {
      paddingLeft: '16px',
      paddingRight: '16px',
    },
  },
});

const ButtonLink = React.forwardRef((props, ref) => <Link innerRef={ref} {...props} />);

interface ITeammatesListProps {
  canEditPermission: boolean;
  classes: any;
  currentApp: FlipdishAPI.App;
  dispatch: (input: any) => void;
  history: any;
  isMe: boolean;
  currentUser: Teammate;
  teammates: FlipdishAPI.Teammate[];
  teammatesTimestamp: number;
  translate: (input: string) => string;
  width: string;
  teammatesLoading: boolean;
}

interface IState {
  invites: any;
  showSnackbar: boolean;
}

class List extends React.Component<ITeammatesListProps, IState> {
  public constructor(props) {
    super(props);
    this.state = {
      invites: {},
      showSnackbar: false,
    };
  }

  public componentWillReceiveProps(newProps) {
    const { teammates } = newProps;
    const { invites } = this.state;

    if (teammates != this.props.teammates) {
      let newInvites = { ...invites };

      for (const teammate of teammates) {
        if (!invites[teammate.TeammateId]) {
          newInvites = {
            ...newInvites,
            [teammate.TeammateId]: false,
          };
        }
      }

      this.setState({ invites: newInvites });
    }
  }

  public handleCloseSnackbar = () => {
    this.setState({ showSnackbar: false });
  };

  public mapAppAccessLevel = (
    appAccessLevel: FlipdishAPI.TeammateBase.AppAccessLevelEnum | undefined
  ): string => {
    const { translate } = this.props;

    switch (appAccessLevel) {
      case FlipdishAPI.TeammateBase.AppAccessLevelEnum.Owner:
        return translate('Owner');
      case FlipdishAPI.TeammateBase.AppAccessLevelEnum.StoreOwner:
        return translate('Store_owner');
      case FlipdishAPI.TeammateBase.AppAccessLevelEnum.ManagedOwner:
        return translate('Managed_owner');
      case FlipdishAPI.TeammateBase.AppAccessLevelEnum.StoreManager:
        return translate('Store_manager');
      case FlipdishAPI.TeammateBase.AppAccessLevelEnum.StoreStaff:
        return translate('Store_staff');
      case FlipdishAPI.TeammateBase.AppAccessLevelEnum.FinanceManger:
        return translate('Finance_manager');
      case FlipdishAPI.TeammateBase.AppAccessLevelEnum.StoreReadOnlyAccess:
        return translate('Store_readonly_access');
      case FlipdishAPI.TeammateBase.AppAccessLevelEnum.Integrator:
        return translate('Integrator');
      default:
        return translate('Owner');
    }
  };

  public resendInvite = (
    teammate: FlipdishAPI.Teammate,
    event: React.MouseEvent<HTMLSpanElement>
  ) => {
    const { dispatch, canEditPermission, currentUser } = this.props;

    event.preventDefault();

    if (!canEditPermission) {
      return;
    }

    this.setState(
      {
        invites: {
          ...this.state.invites,
          [teammate.TeammateId as string]: true,
        },
        showSnackbar: true,
      },
      () => {
        dispatch(
          reinviteTeammate(
            teammate.Email as string,
            teammate.AppAccessLevel as FlipdishAPI.Teammate.AppAccessLevelEnum,
            teammate.StoreIds || []
          )
        );
      }
    );
  };

  public render() {
    const {
      classes,
      currentApp,
      translate,
      teammates,
      width,
      teammatesTimestamp,
      canEditPermission,
      teammatesLoading,
    } = this.props;
    const { invites } = this.state;

    const appId = currentApp != null ? currentApp.AppId : 'global';

    const invitePending = (teammate) => {
      return (
        teammate.InvitationStatus === FlipdishAPI.Teammate.InvitationStatusEnum.Pending &&
        teammate.TeammateId &&
        !invites[teammate.TeammateId]
      );
    };

    const getRowLink = (isMe: boolean, teammate: FlipdishAPI.Teammate) =>
      isMe || !canEditPermission ? undefined : '/' + appId + '/teammates/' + teammate.TeammateId;
    return (
      <PageLayout
        auditLogsFilter={{ type: 'EventType', value: 'teammate.*' }}
        title={translate('Teammates')}
        documentTitle="Teammates"
      >
        <Snackbar
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          open={this.state.showSnackbar}
          onClose={this.handleCloseSnackbar}
          autoHideDuration={2000}
        >
          <SnackbarContent
            className={classes.snackbarContent}
            aria-describedby="client-snackbar"
            message={
              <span id="client-snackbar" className={classes.snackbarSpan}>
                <DoneIcon className={classes.snackbarIcon} />
                {translate('Invite_resent').charAt(0).toUpperCase() +
                  translate('Invite_resent').slice(1)}
              </span>
            }
          />
        </Snackbar>
        <PaperContainer fluid>
          <Hidden smDown>
            <Table className={classes.table}>
              <TableHead>
                <TableRow>
                  <TableCell className={classNames(classes.headCell, classes.cell)}>
                    {translate('Email')}
                  </TableCell>
                  <TableCell className={classNames(classes.headCell, classes.cell)}>
                    {translate('Name')}
                  </TableCell>
                  <TableCell className={classNames(classes.headCell, classes.cell)}>
                    {translate('Role')}
                  </TableCell>
                  <TableCell className={classNames(classes.headCell, classes.cell)}>
                    {translate('Last_active')}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {teammatesLoading ? (
                  <TableRow>
                    <TableCell
                      colSpan={4}
                      className={classNames(
                        classes.bodyCell,
                        classes.bodyCellWithPadding,
                        classes.cell
                      )}
                    >
                      <AppLoadingCircularProgress />
                    </TableCell>
                  </TableRow>
                ) : (
                  teammates.map((teammate, index) => {
                    const isMe =
                      currentApp.AppAccessLevel !== FlipdishAPI.App.AppAccessLevelEnum.Owner &&
                      teammate.AppAccessLevel === FlipdishAPI.App.AppAccessLevelEnum.Owner;

                    return (
                      <TableRow
                        key={index}
                        className={classNames(classes.row, {
                          [classes.rowLink]: !isMe && canEditPermission,
                        })}
                        data-fd={`teammate_${teammate.Email}`}
                      >
                        <TableCellLink
                          component="th"
                          scope="row"
                          to={getRowLink(isMe, teammate)}
                          className={classNames(classes.bodyCell, classes.cell)}
                        >
                          <span className={classes.cellLink}>{teammate.Email}</span>
                        </TableCellLink>
                        <TableCellLink
                          to={getRowLink(isMe, teammate)}
                          className={classNames(classes.bodyCell, classes.cell)}
                        >
                          <span className={classes.cellLink}>{teammate.Name}</span>
                        </TableCellLink>
                        {invitePending(teammate) && (width === 'xs' || width === 'sm') ? (
                          <TableCellLink
                            to={getRowLink(isMe, teammate)}
                            className={classNames(
                              classes.bodyCell,
                              classes.cell,
                              classes.bodyCellWithPadding
                            )}
                          >
                            {this.mapAppAccessLevel(teammate.AppAccessLevel)}
                          </TableCellLink>
                        ) : (
                          <TableCellLink
                            to={getRowLink(isMe, teammate)}
                            className={classNames(classes.bodyCell, classes.cell)}
                          >
                            <span className={classes.cellLink}>
                              {this.mapAppAccessLevel(teammate.AppAccessLevel)}
                            </span>
                          </TableCellLink>
                        )}
                        {invitePending(teammate) ? (
                          <TableCellLink
                            to={getRowLink(isMe, teammate)}
                            className={classNames(
                              classes.bodyCell,
                              classes.cell,
                              classes.bodyCellWithPadding
                            )}
                          >
                            {teammate.LastActivity
                              ? capitalizeFirst(moment(teammate.LastActivity).fromNow())
                              : translate('Never')}
                            {canEditPermission && (
                              <span
                                className={classes.resendInvite}
                                onClick={(event) => this.resendInvite(teammate, event as any)}
                                data-fd={`teammate-reinvite-${teammate.TeammateId}`}
                              >
                                {translate('Resend_invite')}
                              </span>
                            )}
                          </TableCellLink>
                        ) : (
                          <TableCellLink
                            to={getRowLink(isMe, teammate)}
                            className={classNames(classes.bodyCell, classes.cell)}
                          >
                            <span className={classes.cellLink}>
                              {teammate.LastActivity
                                ? capitalizeFirst(moment(teammate.LastActivity).fromNow())
                                : translate('Never')}
                              {teammate.InvitationStatus ===
                              FlipdishAPI.Teammate.InvitationStatusEnum.Pending ? (
                                <span className={classes.inviteResent}>
                                  {translate('Invite_resent')}
                                </span>
                              ) : null}
                            </span>
                          </TableCellLink>
                        )}
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </Hidden>
          <Hidden mdUp>
            <ListMobile
              resendInvite={this.resendInvite}
              teammates={teammates}
              invitePending={invitePending}
              mapAppAccessLevel={this.mapAppAccessLevel}
            />
          </Hidden>
        </PaperContainer>

        <GridContainer>
          {!teammates.length && canEditPermission && !teammatesTimestamp ? (
            <EmptyComponent
              title="Invite_teammates"
              subtitle="Access_stores"
              noLink
              icon={EmptyIcon}
            />
          ) : null}
        </GridContainer>

        <Permissions allowed={[AppResourceSetEnum.EditTeammates]}>
          <Fab
            variant="extended"
            color="secondary"
            className={classes.button}
            to={'/' + appId + '/teammates/new'}
            aria-label="Invite"
            data-fd="teammate-invite"
            component={ButtonLink}
          >
            <AddIcon className={classes.extendedIcon} />
            {translate('Invite')}
          </Fab>
        </Permissions>
      </PageLayout>
    );
  }
}

const mapStateToPropsFactory = () => {
  const getPermissionsSelector = permissionsSelector.hasPermissionFactory(['EditTeammates']);

  return (state: AppState) => {
    const { locale } = state;

    return {
      canEditPermission: getPermissionsSelector(state),
      currentApp: getSelectedApp(state),
      currentUser: getCurrentUser(state),
      teammates: getTeammates(state),
      teammatesLoading: state.teammates?.teammateLoading,
      teammatesTimestamp: getTeammatesTimestamp(state),
      translate: getTranslate(locale),
    };
  };
};

const EnhancedComponent = compose(
  withWidth(),
  withStyles(styles, {
    name: 'List',
    withTheme: true,
  }),
  connect(mapStateToPropsFactory)
)(List);

export { EnhancedComponent as List };
